import type { TFunction } from 'i18next';
import type { AppCategory, ListInstalledAppsResponse, LocalizedText } from '@bliks-io/contracts';

export type InstalledAppEntry = ListInstalledAppsResponse['apps'][number];

export function findInstalledApp(
  apps: InstalledAppEntry[] | undefined,
  appId: string
): InstalledAppEntry | undefined {
  return apps?.find((a) => a.appId === appId);
}

/** Resolve an i18n-capable route label to a string for the given locale. */
export function resolveAppLabel(label: LocalizedText, locale: string): string {
  if (typeof label === 'string') return label;
  return label[locale] ?? label.en ?? Object.values(label)[0] ?? '';
}

/**
 * `satisfies Record<AppCategory, …>` is the point: a new `AppCategorySchema`
 * member leaves a key missing here, so it fails the typecheck instead of
 * silently rendering under some other category's label.
 */
export const CATEGORY_LABEL_KEYS = {
  analysis: 'common.Apps.category.analysis',
  'data-capture': 'common.Apps.category.dataCapture'
} as const satisfies Record<AppCategory, string>;

/**
 * Null for a value outside the enum: nothing re-parses the stored manifest on
 * read, so a registry row written by another backend can carry a category this
 * build has never heard of — and an unlabelled badge is worse than none.
 */
export function appCategoryLabel(category: AppCategory, t: TFunction): string | null {
  const key = CATEGORY_LABEL_KEYS[category] as string | undefined;
  return key ? t(key) : null;
}
